/**
 * Converts the property date_format setting into date-fns format
 * strings for different display contexts.
 *
 * When null/undefined, each function preserves the original hardcoded defaults
 * that existed before this feature was introduced, ensuring backward compatibility.
 */

export type PropertyDateFormat =
  | 'DD/MM/YYYY'
  | 'MM/DD/YYYY'
  | string
  | null
  | undefined;

const isMonthFirst = (propertyDateFormat: PropertyDateFormat): boolean =>
  propertyDateFormat === 'MM/DD/YYYY';

const isDayFirst = (propertyDateFormat: PropertyDateFormat): boolean =>
  propertyDateFormat === 'DD/MM/YYYY';

export const getInputDateFormat = (
  propertyDateFormat: PropertyDateFormat,
): string => {
  if (isMonthFirst(propertyDateFormat)) return 'MM/dd/yyyy';
  return 'dd/MM/yyyy';
};

export const getDateInputPlaceholder = (
  propertyDateFormat: PropertyDateFormat,
): string => {
  if (isMonthFirst(propertyDateFormat)) return 'MM/DD/YYYY';
  return 'DD/MM/YYYY';
};

// "15 Jan" or "Jan 15"
export const getShortDateFormat = (
  propertyDateFormat: PropertyDateFormat,
): string => {
  if (isDayFirst(propertyDateFormat)) return 'dd MMM';
  return 'MMM dd';
};

// "15 Jan 2025" or "Jan 15 2025"
export const getMediumDateFormat = (
  propertyDateFormat: PropertyDateFormat,
): string => {
  if (isDayFirst(propertyDateFormat)) return 'd MMM yyyy';
  return 'MMM d yyyy';
};

// "15 Jan, 2025" or "Jan 15, 2025"
export const getLongDateFormat = (
  propertyDateFormat: PropertyDateFormat,
): string => {
  if (isDayFirst(propertyDateFormat)) return 'dd MMM, yyyy';
  return 'MMM dd, yyyy';
};

export const formatDisplayDate = (
  value: string,
  propertyDateFormat: PropertyDateFormat,
): string => {
  const match = String(value).match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!match) return String(value);
  const [, year, month, day] = match;
  const mm = month.padStart(2, '0');
  const dd = day.padStart(2, '0');
  if (isMonthFirst(propertyDateFormat)) return `${mm}/${dd}/${year}`;
  return `${dd}/${mm}/${year}`;
};
